import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseKey);

// Helper function to get admin API URL
export const getAdminApiUrl = () => {
  return `${supabaseUrl}/functions/v1/admin-api`;
};

export interface AdminUser {
  id: string;
  email: string;
  is_admin: boolean;
  admin_role?: string;
}

export const checkUserAdminStatus = async (userId: string) => {
  try {
    // Get the current session to access the access token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      return { isAdmin: false, adminRole: null, hasMFA: false };
    }

    // Check admin status using the custom admin API
    const response = await fetch(getAdminApiUrl(), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'verifyAdminStatus',
        userId: userId
      })
    });

    // Handle 403 Forbidden (non-admin users) and other errors gracefully
    if (!response.ok) {
      if (response.status === 403) {
        // User is not admin - this is expected for non-admin users
        return { isAdmin: false, adminRole: null, hasMFA: false };
      }
      // For other errors, also return false but log the error
      return { isAdmin: false, adminRole: null, hasMFA: false };
    }

    const result = await response.json();
    const isAdmin = result.isAdmin === true;
    const adminRole = result.adminRole || null;
    
    // Check MFA status using Supabase's built-in method if user is admin
    let hasMFA = false;
    if (isAdmin) {
      try {
        const { data: mfaData, error: mfaError } = await supabase.auth.mfa.listFactors();
        if (!mfaError && mfaData) {
          // Combine all factor types and check for verified factors
          const allFactors = [
            ...(mfaData.totp || []),
            ...(mfaData.phone || [])
          ];
          hasMFA = allFactors.some((factor: any) => factor.status === 'verified');
        }
      } catch (mfaError) {
        hasMFA = false;
      }
    }
    
    return {
      isAdmin,
      adminRole,
      hasMFA
    };
  } catch (error) {
    return { isAdmin: false, adminRole: null, hasMFA: false };
  }
};
